import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { signIn, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, resetPassword } from '../services/cognito';
import { api } from '../services/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  
  const [mode, setMode] = useState<'signin' | 'signup' | 'confirm' | 'forgot-password' | 'reset-password'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const session = await signIn({ email, password });
      
      // Extract user info from session
      const idToken = session.getIdToken();
      const payload = idToken.decodePayload();
      
      // Parse groups - Cognito can return string or array
      const groupsRaw = payload['cognito:groups'];
      const groups = !groupsRaw 
        ? [] 
        : Array.isArray(groupsRaw) 
        ? groupsRaw 
        : [groupsRaw];

      const user = {
        userId: payload.sub,
        email: payload.email,
        givenName: payload.given_name || '',
        familyName: payload.family_name || '',
        groups: groups,
      };

      setAuth(
        user,
        session.getAccessToken().getJwtToken(),
        session.getIdToken().getJwtToken()
      );

      // Record login for activity tracking
      try {
        await api.recordLogin();
      } catch (err) {
        // Silently fail - don't prevent login if activity tracking fails
      }

      // Navigate based on user group
      if (user.groups.includes('pending')) {
        navigate('/pending');
      } else if (user.groups.includes('super-admin') || user.groups.includes('admin') || user.groups.includes('member')) {
        navigate('/feed');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      console.error('❌ Sign in error:', err);
      
      // Handle unconfirmed user - auto-recovery flow
      if (err.code === 'UserNotConfirmedException' || err.name === 'UserNotConfirmedException') {
        setError('');
        setSuccessMessage('Your account is not verified yet. We\'ve sent a new verification code to your email.');
        try {
          await resendConfirmationCode(email);
          setMode('confirm');
        } catch (resendErr: any) {
          console.error('❌ Failed to resend verification code:', resendErr);
          setError('Your account needs verification, but we couldn\'t resend the code. Please contact support.');
        }
      }
      // Handle user not found
      else if (err.code === 'UserNotFoundException' || err.name === 'UserNotFoundException') {
        setError('No account found with this email. Please sign up first.');
      }
      // Handle incorrect password
      else if (err.code === 'NotAuthorizedException' || err.name === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
      }
      // All other errors
      else {
        setError(err.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signUp({ email, password, givenName, familyName });
      setMode('confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Confirm the email
      await confirmSignUp(email, confirmationCode);
      
      // Sign in automatically to get session
      const session = await signIn({ email, password });
      
      // Note: User is automatically added to pending group via Cognito POST_CONFIRMATION trigger
      // This API call is a fallback in case the trigger hasn't completed yet
      try {
        await api.addUserToPendingGroup();
      } catch (err: any) {
        // Non-blocking: The POST_CONFIRMATION trigger should handle this automatically
      }
      
      // Set auth state
      const idToken = session.getIdToken();
      const accessToken = session.getAccessToken();
      
      // Parse groups - Cognito can return string or array
      const groupsRaw = idToken.payload['cognito:groups'];
      const userGroups = !groupsRaw 
        ? [] 
        : Array.isArray(groupsRaw) 
        ? groupsRaw 
        : [groupsRaw];
      
      setAuth(
        {
          userId: idToken.payload.sub,
          email: idToken.payload.email,
          givenName: idToken.payload.given_name || '',
          familyName: idToken.payload.family_name || '',
          groups: userGroups,
        },
        accessToken.getJwtToken(),
        idToken.getJwtToken()
      );
      
      // Navigate to pending page (user needs admin approval)
      navigate('/pending');
    } catch (err: any) {
      setError(err.message || 'Failed to confirm account');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccessMessage('');
    setResendingCode(true);

    try {
      await resendConfirmationCode(email);
      setSuccessMessage('Verification code resent! Check your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setResendingCode(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setMode('reset-password');
      setSuccessMessage('Reset code sent to your email!');
    } catch (err: any) {
      setError(err.message || 'Failed to initiate password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validate passwords match
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, resetCode, newPassword);
      setSuccessMessage('Password reset successful! You can now sign in.');
      setMode('signin');
      setPassword(''); // Clear old password
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    setError('');
    setSuccessMessage('');
    setResendingCode(true);

    try {
      await forgotPassword(email);
      setSuccessMessage('Reset code resent! Check your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setResendingCode(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/castle.jpg)',
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-3 drop-shadow-lg">
            {/* OPTION 2 & 3: Wrenches on login page */}
            <span className="text-3xl">🪛</span>
            <span>Philips Family Album</span>
            <span className="text-3xl">🪛</span>
          </h1>
          <p className="text-white/90 text-lg drop-shadow-md">
            {mode === 'signin' && 'Sign in to access your family photos'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'confirm' && 'Confirm your email address'}
            {mode === 'forgot-password' && 'Reset your password'}
            {mode === 'reset-password' && 'Enter your reset code'}
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {successMessage}
            </div>
          )}

          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setMode('forgot-password')}
                  className="text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <p className="text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Sign up
                </button>
              </p>
            </form>
          )}

          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={givenName}
                    onChange={(e) => setGivenName(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 8 characters with uppercase, lowercase, and numbers
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating account...' : 'Sign Up'}
              </button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('signin')}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Sign in
                </button>
              </p>
            </form>
          )}

          {mode === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                We've sent a confirmation code to <strong>{email}</strong>. Please enter it below.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmation Code
                </label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="123456"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendingCode}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingCode ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('signin')}
                  className="text-gray-600 hover:text-gray-700 hover:underline font-medium"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {mode === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Enter your email address and we'll send you a code to reset your password.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending code...' : 'Send Reset Code'}
              </button>

              <p className="text-center text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => setMode('signin')}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Back to sign in
                </button>
              </p>
            </form>
          )}

          {mode === 'reset-password' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                We've sent a reset code to <strong>{email}</strong>. Enter it below with your new password.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reset Code
                </label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="123456"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 placeholder:text-gray-400"
                  placeholder="••••••••"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 8 characters with uppercase, lowercase, and numbers
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting password...' : 'Reset Password'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResendResetCode}
                  disabled={resendingCode || loading}
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendingCode ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={() => setMode('signin')}
                  className="text-gray-600 hover:text-gray-700 hover:underline font-medium"
                >
                  Back to sign in
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

